import type { Schema, SchemaData, SerializedSessionWithHistory } from '@lemonade-hq/chat-engine';
import { Client } from '@lemonade-hq/nuiza';
import { devWarn } from '@lemonade-hq/ts-helpers';
import type { AxiosRequestConfig } from 'axios';
import { appendQueryParams, createTrackingHeaders } from './utils';

export type ChatEngineSession<TPresentation = undefined> = SerializedSessionWithHistory<TPresentation>;

export interface GetPageRequestPayload {
  readonly pageName: string;
  readonly initData?: Record<string, unknown>;
}

export interface PageSubmitAnswersRequestPayload {
  readonly pageName: string;
  readonly answers: SchemaData;
}

export interface PageFinishRequestPayload {
  readonly pageName: string;
}

interface PageMechanismClientData {
  readonly serverBaseUrl: string;
  readonly accessTokenOverride?: string | null;
  readonly emailCode?: string | null;
}

export interface PageRenderResponse<TPresentation> {
  readonly pageName: string;
  readonly auth: ChatEngineSession<TPresentation>['auth'];
  readonly paramsSchema: Partial<Schema>;
  readonly updateSchema: Partial<Schema>;
  readonly assets: string[];
  readonly renderResult: ChatEngineSession<TPresentation>['history'][number]['renderedStep'];
}

export interface FinishResponse {
  readonly renderResult?: { readonly redirectTo?: string };
}

export class PageMechanismClient<TPresentation> {
  constructor(private readonly chatEngineClientData: PageMechanismClientData) {}

  async getPage(
    { pageName, initData }: GetPageRequestPayload,
    options?: AxiosRequestConfig,
  ): Promise<PageRenderResponse<TPresentation>> {
    const { serverBaseUrl, accessTokenOverride, emailCode } = this.chatEngineClientData;

    const trackingHeaders = createTrackingHeaders();

    const path = new URL(`${serverBaseUrl}/chat/public/pages/${pageName}`);

    return await Client.get<PageRenderResponse<TPresentation>>(
      appendQueryParams(path, accessTokenOverride, emailCode, initData),
      // @ts-expect-error: need to upg nuiza client
      { ...options, headers: { ...trackingHeaders, ...options?.headers } },
    );
  }

  async submitAnswers(
    { pageName, answers }: PageSubmitAnswersRequestPayload,
    options?: AxiosRequestConfig,
  ): Promise<PageRenderResponse<TPresentation> | null> {
    const { serverBaseUrl, accessTokenOverride } = this.chatEngineClientData;

    const trackingHeaders = createTrackingHeaders();

    const updatedKeys = Object.keys(answers);
    if (updatedKeys.length === 0) {
      devWarn('No updated keys provided');
      return null;
    }

    if (updatedKeys.length > 1) {
      devWarn(`Only one updated key is allowed. Ignored the rest: ${updatedKeys.slice(1).join(', ')}`);
    }

    const path = new URL(`${serverBaseUrl}/chat/public/pages/${pageName}`);
    return await Client.put<PageRenderResponse<TPresentation>>(
      appendQueryParams(path, accessTokenOverride),
      {
        data: {
          updateKey: updatedKeys[0],
          updatePayload: answers,
        },
      },
      // @ts-expect-error: need to upg nuiza client
      { ...options, headers: { ...trackingHeaders, ...options?.headers } },
    );
  }

  async finish({ pageName }: PageFinishRequestPayload, options?: AxiosRequestConfig): Promise<FinishResponse> {
    const { serverBaseUrl } = this.chatEngineClientData;

    const path = new URL(`${serverBaseUrl}/chat/public/pages/${pageName}/finish`);

    return await Client.get<FinishResponse>(
      path.toString(),
      // @ts-expect-error: need to upg nuiza client
      options,
    );
  }
}
